/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import axios, { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import i18next from 'i18next';

import { TDocumentDefinitions } from 'interfaces/general';

/**
 * Elimina los campos vacíos de un objeto.
 * @param {object} object - Objeto a limpiar.
 * @returns {object} Objeto sin campos vacíos.
 */
export function clearEmptyFields(object: { [key: string]: string | unknown }) {
  Object.keys(object || {}).forEach((field) => {
    if (object[field] === '' || object[field]?.length === 0) {
      delete object[field];
    }
  });
  return object;
}

/**
 * Establece todos los campos de un objeto como vacíos o null.
 * @param {object} object - Objeto a modificar.
 * @returns {object} Objeto con campos vacíos o null.
 */
export function setFieldsToEmpty(object: { [key: string]: unknown }) {
  const copy = { ...object };
  Object.keys(copy).forEach((key: string) => {
    copy[key] = typeof copy[key] === 'number' ? null : '';
  });
  return copy;
}

/**
 * Calcula el color opuesto (blanco o negro) según la luminancia de un color hexadecimal.
 * @param {string} hexColor - Color en formato hexadecimal.
 * @returns {string} Color opuesto (#000000 o #ffffff).
 */
export function getOppositeColor(hexColor: string): string {
  if (hexColor) {
    const r = parseInt(hexColor.substring(1, 3), 16);
    const g = parseInt(hexColor.substring(3, 5), 16);
    const b = parseInt(hexColor.substring(5, 7), 16);
    const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return y > 128 ? '#000000' : '#ffffff';
  }
  return '';
}

/**
 * Convierte un archivo File a una cadena base64.
 * @param {File} file - Archivo a convertir.
 * @returns {Promise<string|ArrayBuffer>} Promesa con el string base64.
 */
export function getBase64(file: File): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
}

/**
 * Descarga un archivo en base64.
 * @param {string} base64DataUri - Data URI en base64 del archivo.
 * @param {string} filename - Nombre del archivo a descargar.
 */
export function downloadBase64File(base64DataUri: string, filename: string) {
  const link = document.createElement('a');
  link.href = base64DataUri;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Descarga un archivo desde una URL.
 * @param {string} url - URL del archivo.
 * @param {string} filename - Nombre del archivo a descargar.
 */
export async function downloadFileByUrl(
  url: string,
  filename: string
): Promise<void> {
  try {
    const response: AxiosResponse<BlobPart> = await axios({
      url,
      method: 'GET',
      responseType: 'blob'
    });
    const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    await Promise.resolve();
  } catch (error) {
    await Promise.reject(error);
  }
}

/**
 * Descarga un archivo PDF generado con pdfMake.
 * @param {string} fileTitle - Nombre del archivo PDF.
 * @param {TDocumentDefinitions} docDefinition - Definición del documento PDF.
 */
export function downloadPdf(
  fileTitle: string,
  docDefinition: TDocumentDefinitions
) {
  const { pdfMake } = window;
  pdfMake.vfs = window.pdfMake.vfs;

  pdfMake.createPdf(docDefinition).download(fileTitle);
}

/**
 * Genera un PDF en base64 usando pdfMake.
 * @param {TDocumentDefinitions} docDefinition - Definición del documento PDF.
 * @returns {Promise<string>} Promesa con el PDF en base64.
 */
export function createPdfBase64(
  docDefinition: TDocumentDefinitions
): Promise<string> {
  return new Promise((resolve, reject) => {
    const { pdfMake } = window;
    pdfMake.vfs = window.pdfMake.vfs;
    pdfMake.createPdf(docDefinition).getBase64(
      (data: string) => {
        resolve(data);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

/**
 * Reglas de validación comunes para formularios.
 * @returns {object} Reglas y validadores.
 */
export function validationRules() {
  const length = 150;

  const maxLength = {
    value: length,
    message: i18next.t('common.commonMessages.maxLength', {
      ns: 'global',
      number: length
    })
  };

  const pattern = {
    value: /^[a-zA-Z0-9\s-ñÑáÁéÉíÍóÓúÚ_]*$/,
    message: i18next.t('common.commonMessages.spetialCaracters', {
      ns: 'global'
    })
  };

  const patternNumber = {
    value: /^[0-9]*$/,
    message: i18next.t('common.commonMessages.onlyNumbers', {
      ns: 'global'
    })
  };

  const patternNumberWith2decimal = {
    value: /^(\-)?[0-9]+(?:(?:\.[0-9]{1,2})?)$/,
    message: i18next.t('common.commonMessages.twoDecimals', {
      ns: 'global'
    })
  };

  const patternNumberWith7NumbersAnd2Decimals = {
    // maximo 7 enteros y 2 decimales
    value: /^(\-)?[0-9]{1,7}(?:\.[0-9]{1,2})?$/,
    message: i18next.t('common.commonMessages.sevenWholeNumberstwoDecimals', {
      ns: 'global'
    })
  };

  const validateSpaces = (value?: string) => {
    if (value?.length && value?.trim() === '') {
      // return 'El valor no puede ser solo espacios.';
      return i18next.t('common.commonMessages.onlySpaceError', {
        ns: 'global'
      });
    }
    if (value?.length && value[0] === ' ') {
      return i18next.t('common.commonMessages.initialSpaceError', {
        ns: 'global'
      });
    }
    if (value?.length > 1 && value[value.length - 1] === ' ') {
      return i18next.t('common.commonMessages.endingSpaceError', {
        ns: 'global'
      });
    }
  };

  const validateCantContainSpaces = (value?: string) => {
    if (value?.length && value?.includes(' ')) {
      return i18next.t('common.commonMessages.spaceError', {
        ns: 'global'
      });
    }
  };

  /* Validación de psw */
  const validationText = (value?: string) => {
    const oneMayus = '^(?=.*[A-Z]).*$';
    const validationOneMayus = new RegExp(oneMayus);

    const oneMinus = '^(?=.*[a-z]).*$';
    const validationOneMinus = new RegExp(oneMinus);

    if (!validationOneMayus.exec(value)) {
      return i18next.t('common.commonMessages.oneUpperAtLeast', {
        ns: 'global'
      });
    }
    if (!validationOneMinus.exec(value)) {
      return i18next.t('common.commonMessages.oneUpperAtLeast', {
        ns: 'global'
      });
    }
  };

  const validationNumericAndSpecial = (value?: string) => {
    const oneNumeric = '^(?=.*[0-9]).*$';
    const validationOneNumeric = new RegExp(oneNumeric);

    const oneSpecial = '^(?=.*[~"`\\[\\]\\^"\\\\/\'(){}.@$!%*&]).*$';
    const validationOneSpecial = new RegExp(oneSpecial);

    if (!validationOneNumeric.exec(value)) {
      return i18next.t('common.commonMessages.numberBetween', {
        ns: 'global'
      });
    }
    if (!validationOneSpecial.exec(value)) {
      return i18next.t('common.commonMessages.allowedSpetialCaracters', {
        ns: 'global'
      });
    }
  };

  const validationSpace = (value?: string) => {
    const spaceInitial = /^\S/;
    const validationSpaceInitial = new RegExp(spaceInitial, 'gm');

    const spaceEnd = /\S$/;
    const validationSpaceEnd = new RegExp(spaceEnd, 'gm');

    if (!validationSpaceInitial.exec(value)) {
      return 'Debe ir sin espacio al principio';
    }
    if (!validationSpaceEnd.exec(value)) {
      return 'Debe ir sin espacio al final';
    }
  };
  /* Validación de psw */

  /* función para validación psw */
  const validateRulesNewPassword = (value?: string) => {
    if (validationText(value)) {
      return validationText(value);
    }
    if (validationNumericAndSpecial(value)) {
      return validationNumericAndSpecial(value);
    }
    if (validationSpace(value)) {
      return validationSpace(value);
    }
  };

  const validateDate = (value?: string | Date, greaterThan = '') => {
    if (!value) value = undefined;
    const date = dayjs(value);
    if (!date.isValid()) {
      return i18next.t('common.commonMessages.invalidDate', {
        ns: 'global'
      });
    }

    if (greaterThan && date.isBefore(dayjs(greaterThan))) {
      return i18next.t('common.commonMessages.dateGreaterThan', {
        ns: 'global',
        date: greaterThan
      });
    }
  };

  return {
    maxLength,
    pattern,
    patternNumber,
    patternNumberWith2decimal,
    patternNumberWith7NumbersAnd2Decimals,
    validateDate,
    validateSpaces,
    validateCantContainSpaces,
    validateRulesNewPassword
  };
}

/** Funcion para validar las fechas cuando es un ticket programado creado por carga masiva externa (al final del dia) */
/** Esto se hace ya que por un tema de horario de base de datos, cuando se devuelve la informacion en
 * el servicio, hay una diferencia de varias horas y se quiere evitar que por ejemplo; si la fecha de inicio
 * es 31/08/2023, no se muestre 01/09/2023 en la Tabla de busqueda de ticket, esto por hacer el Formato UTC Local. */
export function getDateUTCformatWhenDateIsProgrammed(date: string) {
  if (date) {
    const dateFormat = dayjs(date).format('DD-MM-YYYY');
    const dateAndTimeUTCLocalFormat = dayjs
      .utc(date)
      .local()
      .format('DD-MM-YYYY HH:mm');
    const timeUTCLocalFormat = dayjs.utc(date).local().format('HH:mm');

    if (dateAndTimeUTCLocalFormat.includes(dateFormat)) {
      return dateAndTimeUTCLocalFormat;
    }
    return `${dateFormat} ${timeUTCLocalFormat}`;
  }
  return '';
}

export function hasValue(obj: unknown) {
  return Object.values(obj).some(
    (value) => value !== null && value !== undefined && value?.length !== 0
  );
}

export function handleMediaQuery(mediaQuery: string, callback: () => void) {
  if (mediaQuery) {
    const mediaquery = window.matchMedia(mediaQuery);
    mediaquery.onchange = (event) => {
      if (event.matches) callback();
    };
  }
}

/**
 * Merges two arrays, ensuring uniqueness based on a specified property.
 *
 * @template T - The type of elements in the arrays
 * @param {T[]} array1 - The first array to merge
 * @param {T[]} array2 - The second array to merge
 * @param {keyof T} idName - The name of the property to use as the identifier for uniqueness
 * @returns {T[]} - The merged array with unique elements
 */
export const mergeArrays = <T>(
  array1: T[],
  array2: T[],
  idName: keyof T
): T[] => {
  const mergedArray = [...array1];

  (array2 || []).forEach((item) => {
    if (!array1?.some((element) => element[idName] === item[idName])) {
      mergedArray.push(item);
    }
  });

  return mergedArray;
};

/**
 * Removes duplicate elements from an array based on a specified property.
 *
 * @template T - The type of elements in the array
 * @param {T[]} array - The array from which to remove duplicates
 * @param {keyof T} idName - The name of the property to use as the identifier for uniqueness
 * @returns {T[]} - The array with duplicates removed
 */
export const removeDuplicates = <T>(array: T[], idName: keyof T): T[] => {
  const uniqueArray = array.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t[idName] === item[idName])
  );

  return uniqueArray;
};

export const removeEmojis = (text: string) =>
  typeof text === 'string'
    ? text
        ?.replace(
          // eslint-disable-next-line max-len
          /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
          ''
        )
        .trim()
    : text;

const isColorTooLight = (color: string) => {
  // Convertir el color hexadecimal a RGB
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  // Calcular el brillo del color usando la fórmula de luminosidad relativa
  const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Definir el umbral para los colores claros
  return brightness > 200; // Ajusta el umbral según sea necesario
};

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color;
  do {
    color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
  } while (isColorTooLight(color));
  return color;
};

export default {
  clearEmptyFields,
  createPdfBase64,
  downloadBase64File,
  downloadFileByUrl,
  downloadPdf,
  getBase64,
  getDateUTCformatWhenDateIsProgrammed,
  getOppositeColor,
  handleMediaQuery,
  hasValue,
  mergeArrays,
  setFieldsToEmpty,
  validationRules,
  removeDuplicates,
  removeEmojis,
  getRandomColor
};
